/* eslint-disable max-lines */
import type { AfterViewInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, of, Subject, switchMap } from 'rxjs';
import { map, filter, startWith, takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account-data';
import type { BillingInformation } from '@recruitee/billing-information-types';
import { filterBoolean, filterDefined } from '@recruitee/common';
import {
  PrimaryContactEntityActions,
  PrimaryContactEntitySelectors,
} from '@recruitee/company-data';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { SubscriptionPricingSelectors } from '@recruitee/pricing-data';
import { Currency } from '@recruitee/pricing-types';
import type { BillingPlan, Price } from '@recruitee/products-types';
import type { Addon, PaymentInterval } from '@recruitee/subscription-types';
import {
  WindowRef,
  ButtonLoadingDirective,
  ButtonComponent,
  IconComponent,
  MessageComponent,
  BoxComponent,
  SpinnerComponent,
} from '@recruitee/user-interface';

import { ProductOrderEstimateService } from '../../../services/product-order-estimate.service';
import { BillingAddressFormComponent } from '../../../shared/billing-address-form/billing-address-form.component';
import { BillingContactFormComponent } from '../../../shared/billing-contact-form/billing-contact-form.component';
import type { PricingFeatureLoadedModel } from '../../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../../types/pricing-feature.types';
import type { Breadcrumb } from '../product-order-header/product-order-header.component';
import { ProductOrderHeaderComponent } from '../product-order-header/product-order-header.component';
import { ProductOrderSummaryComponent } from '../product-order-summary/product-order-summary.component';
import { ProductOrderParamsIT, ProductOrderStepUrl } from '../product-order.types';

type BillingInformationFormError = { title?: string; description: string; withContact?: boolean };

@Component({
  selector: 'rt-product-order-billing-step',
  templateUrl: './product-order-billing-step.component.html',
  styleUrls: ['./product-order-billing-step.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductOrderHeaderComponent,
    ProductOrderSummaryComponent,
    BoxComponent,
    BillingAddressFormComponent,
    BillingContactFormComponent,
    MessageComponent,
    IconComponent,
    ButtonComponent,
    RouterLink,
    ButtonLoadingDirective,
    SpinnerComponent,
    TranslatePipe,
  ],
})
export class ProductOrderBillingStepComponent implements AfterViewInit {
  @ViewChild(BillingAddressFormComponent, { static: false })
  public set addressForm(component: BillingAddressFormComponent) {
    if (component) {
      this.combinedForm.setControl(0, component.form);
    }
  }

  @ViewChild(BillingContactFormComponent, { static: false })
  public set contactForm(component: BillingContactFormComponent) {
    if (component) {
      this.combinedForm.setControl(1, component.form);
    }
  }

  public currentUserId: number;
  public slotsUsed: number;
  public slots: number | undefined;
  public quantity: number | undefined;
  public addons: Addon[];
  public interval: PaymentInterval;
  public product: BillingPlan;
  public price?: Price;
  public pricingModel: PricingFeatureLoadedModel;
  public billingInformation: Partial<BillingInformation>;
  public billingContactFields: string[];
  public breadcrumbs: Breadcrumb[];
  public companyName: string;
  public proceeding: boolean;
  public valid: boolean;
  public value: Partial<BillingInformation>;
  public error: BillingInformationFormError | undefined;

  private ngOnDestroy$ = new Subject<void>();
  private combinedForm = new UntypedFormArray([]);
  private params$ = inject(ProductOrderParamsIT);

  private errorsMap: Record<string, BillingInformationFormError> = {
    billing_information_invalid_vat: {
      title: this.i18n.translate(
        'app.pricing.product_order.errors.billing_information_invalid_vat.title',
      ),
      description: this.i18n.translate(
        'app.pricing.product_order.errors.billing_information_invalid_vat.description',
      ),
    },
    billing_information_invalid_zip: {
      description: this.i18n.translate(
        'app.pricing.product_order.errors.billing_information_invalid_zip.description',
      ),
    },
    invalid_vat_number: {
      title: this.i18n.translate('app.pricing.product_order.errors.invalid_vat_number.title'),
      description: this.i18n.translate(
        'app.pricing.product_order.errors.invalid_vat_number.description',
      ),
    },
    state_code: {
      title: this.i18n.translate('app.pricing.product_order.errors.invalid_state_code.title'),
      description: this.i18n.translate(
        'app.pricing.product_order.errors.invalid_state_code.description',
      ),
    },
    unknown_error: {
      description: this.i18n.translate(
        'app.pricing.product_order.errors.unknown_error.description',
      ),
    },
    validation_failed: {
      title: this.i18n.translate('app.pricing.product_order.errors.validation_failed.title'),
      description: this.i18n.translate(
        'app.pricing.product_order.errors.validation_failed.description',
      ),
    },
  };

  constructor(
    private cdRef: ChangeDetectorRef,
    private vcRef: ViewContainerRef,
    private route: ActivatedRoute,
    private router: Router,
    private facade: PricingFeatureFacade,
    private model$: PricingFeatureObservableModel,
    private i18n: I18nService,
    private windowRef: WindowRef,
    private store: Store,
    private estimateService: ProductOrderEstimateService,
  ) {}

  public next() {
    this.proceeding = true;
    this.cdRef.detectChanges();

    this.facade
      .updateBillingData({ ...this.value, currency: this.product.currency })
      .pipe(
        switchMap(ok => {
          if (ok) {
            return this.estimateService.loadData$();
          } else {
            return of(false);
          }
        }),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(
        ok => {
          if (ok) {
            this.store.dispatch(PrimaryContactEntityActions.fetch());
            this.router.navigate(['../', ProductOrderStepUrl.OrderPayment], {
              relativeTo: this.route,
              state: {
                downgradeScreenViewed:
                  this.windowRef.nativeWindow.history.state.downgradeScreenViewed,
                migrationScreenViewed:
                  this.windowRef.nativeWindow.history.state.migrationScreenViewed,
              },
            });
          } else {
            this.proceeding = false;
            this.cdRef.detectChanges();
          }
        },
        error => {
          this.onError(error);
          this.proceeding = false;
          this.cdRef.detectChanges();
        },
      );
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit() {
    this.store.dispatch(PrimaryContactEntityActions.fetch());

    combineLatest([
      this.params$,
      this.model$.pipe(filter<PricingFeatureLoadedModel>(({ loaded }) => loaded)),
      this.facade.getBillingPlansWithActive(),
      this.store.select(AccountSelectors.currentCompanyWithUser).pipe(filterBoolean),
      this.store.select(SubscriptionPricingSelectors.data).pipe(
        filterDefined,
        map(subscription => subscription.addons),
      ),
      this.store.select(PrimaryContactEntitySelectors.data).pipe(filterBoolean),
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([params, model, billingPlans, companyWithUser, addons, primaryContact]) => {
        const { productId, quantity, interval, slots } = params;
        const product = billingPlans.find(p => p.id === productId);
        if (!product) {
          throw new Error(`BillingPlan with id "${productId}" not found`);
        }

        this.product = product;
        this.price = quantity
          ? this.product.prices.find(
              price =>
                (price.fteRangeEnd !== null
                  ? price.fteRangeStart <= quantity && price.fteRangeEnd >= quantity
                  : price.fteRangeStart <= quantity) && price.billingPeriodMonths === interval,
            )
          : undefined;
        this.billingInformation = model.billingInformation || {};
        this.slots = slots;
        this.quantity = quantity;
        this.pricingModel = model;
        this.interval = <PaymentInterval>interval;
        this.billingContactFields = [
          'email',
          'invoiceEmails',
          ...(!primaryContact.adminId ? ['primaryContactId'] : []),
        ];
        this.currentUserId = companyWithUser.currentUser.id;
        this.slotsUsed = model.jobSlotsUsed;
        this.companyName = companyWithUser.currentCompany?.name!;
        this.addons = addons;

        this.breadcrumbs = this.getBreadcrumbs();
        this.cdRef.detectChanges();
      });
  }

  public ngAfterViewInit(): void {
    this.combinedForm.statusChanges
      .pipe(startWith(this.combinedForm.status), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(status => {
        this.valid = status === 'VALID';
        this.breadcrumbs = this.getBreadcrumbs();
        this.cdRef.detectChanges();
      });

    this.combinedForm.valueChanges
      .pipe(startWith(this.combinedForm.value), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([address, contact]) => {
        const invoiceEmailsRaw: Array<{ isValid: boolean; email: string }> = contact?.invoiceEmails;

        this.value = {
          ...address,
          ...contact,
          invoiceEmails: Array.isArray(invoiceEmailsRaw)
            ? invoiceEmailsRaw.filter(({ isValid }) => isValid).map(({ email }) => email)
            : null,
        };

        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  public onError(error: {
    message: {
      errorCode?: string;
      errorMessage: string;
      errorFields: Record<string, string | string[]>;
    };
  }): void {
    const { errorCode, errorMessage, errorFields } = error.message;

    if (errorCode) {
      if (errorCode === 'validation_failed' && 'email' in errorFields) {
        const { email } = errorFields;
        const description =
          typeof email === 'string' ? email : this.errorsMap['unknown_error'].description;
        this.error = { description, withContact: false };
      } else if (errorCode === 'validation_failed' && 'stateCode' in errorFields) {
        this.error = this.errorsMap['state_code'];
      } else if (errorCode === 'validation_failed' && 'vatNumber' in errorFields) {
        const { vatNumber } = errorFields;
        const description =
          Array.isArray(vatNumber) && vatNumber.length > 0
            ? vatNumber[0]
            : this.errorsMap['unknown_error'].description;
        this.error = { description, withContact: true };
      } else if (errorCode === 'validation_failed' && 'base' in errorFields) {
        const { base } = errorFields;
        const description =
          Array.isArray(base) && base.length > 0
            ? base[0]
            : this.errorsMap['unknown_error'].description;
        this.error = { description, withContact: false };
      } else {
        const error = this.errorsMap[errorCode];
        this.error = {
          title: errorMessage ? errorMessage : error.title,
          ...error,
          withContact: true,
        };
      }
    } else {
      const error = this.errorsMap['unknown_error'];
      this.error = {
        ...error,
        withContact: true,
      };
    }
  }

  public addMoreSlotsDialog(): void {
    this.facade.openSlotsPickerDialog(
      this.product,
      this.pricingModel,
      this.pricingModel.subscription?.manual,
      this.vcRef,
    );
  }

  public contactSupport(): void {
    this.facade.openIntercom();
  }

  public changeCurrency(currency: Currency) {
    this.facade.changeCurrency(
      Currency[currency],
      this.product,
      this.pricingModel,
      this.interval,
      this.slots,
      this.quantity,
    );
  }

  private getBreadcrumbs(): Breadcrumb[] {
    return [
      {
        name: this.i18n.translate('app.pricing.product_order.breadcrumb_plan_picker'),
        url: [ProductOrderStepUrl.ProductPicker],
        disabled: false,
        icon: 'tag-text-outline',
      },
      {
        name: this.i18n.translate('app.pricing.product_order.breadcrumb_billing'),
        url: ['../', ProductOrderStepUrl.OrderBilling],
        disabled: false,
        icon: 'short-text',
      },
      {
        name: this.i18n.translate('app.pricing.product_order.breadcrumb_payment'),
        url: ['../', ProductOrderStepUrl.OrderPayment],
        disabled: true,
        icon: 'credit-card',
      },
    ];
  }
}
